import { Component } from 'preact'
import Card from '../cards/index'
import Phrase from '../../libs/translationsUtils/phrase'
import { CategoryInfo, ParentCategoryInfo } from '../mega-menu/Types'
import { z } from 'zod'
import { GetServerSideProps } from '../../server-middleware/context'
import { getCategories, LegacyCategoryType, prepCategories } from '../../server-middleware/categoryRepository'
import WidgetContainer, { widgetContainerConfig } from '../widget-container'
import classnames from 'classnames'

interface KnowledgeBaseWidgetProps {
  config: KnowledgeBaseWidgetConfig
  categories: ParentCategoryInfo[]
}

export const config = z.object({
  selectedTemplate: z.number(),
  displayOnlyParent: z.boolean()
}).merge(widgetContainerConfig)
  .default({
    selectedTemplate: 0,
    displayOnlyParent: true
  })

export type KnowledgeBaseWidgetConfig = z.infer<typeof config>

export const widgetName = 'knowledge_base' as const

export const getServerSideProps: GetServerSideProps<KnowledgeBaseWidgetConfig, KnowledgeBaseWidgetProps> = async (ctx, config) => {
  return await ctx.cache(`kb-category_${JSON.stringify(config)}_${ctx.userId}`, async () => {
    const list = await prepCategories(
      ctx,
      await getCategories(ctx, [LegacyCategoryType.knowledgeBase])
    )

    return {
      config: config,
      categories: list
    }
  })
}

class KnowledgeBaseWidget extends Component<KnowledgeBaseWidgetProps, unknown> {
  constructor (props: KnowledgeBaseWidgetProps) {
    super(props)
    this.getMetadata = this.getMetadata.bind(this)
  }

  templates = ['big-card', 'small-card']

  getMetadata (categories: CategoryInfo[]) {
    let totalTopicCount = 0

    categories.forEach(category => {
      totalTopicCount += category.topicCount
    })

    return (
      <ul className='list--dot-seperated knowledge-base__card-metadata'>
        <li className='qa-forum-overview-category-counter'>
          {categories.length} <Phrase
            m='Forum' p='knowledgebase.category'
            count={{ n: categories.length.toString() }}
                              />
        </li>
        <li className='qa-forum-overview-post-counter'>
          <Phrase
            m='Common' p='knowledge.base.articles'
            count={{ n: totalTopicCount.toString() }}
          />
        </li>
      </ul>
    )
  }

  render () {
    const { config, categories } = this.props
    const { title, selectedTemplate, displayOnlyParent } = config
    if (!categories.length) {
      return null
    }

    return (
      <WidgetContainer
        widgetName='knowledge-base'
        config={config}
      >
        <div
          className={classnames(
            'full-width Template-brand-featured knowledge-base-container qa-knowledge-base-container custom-knowledge-base'
          )}
        >
          <div className='knowledge-base-category-list'>
            {displayOnlyParent ? (
              categories.map((category, idx) => (
                <Card
                  key={category.id}
                  customClass={`knowledge-base__card knowledge-base__card-${
                    this.templates[selectedTemplate || 0]
                  }`}
                  idx={idx}
                  title={category.title}
                  metadata={this.getMetadata(category.subforums)}
                  imageSrc={category.thumbnailUrl}
                  navigation={{
                    url: category.url
                  }}
                />
              ))
            ) : (
              <div className='knowledge-base_parent-child-component-wrapper'>
                {categories.map((category) => (
                  <section className='knowledge-base_parent-child-component' key={category.id}>
                    {title ? (
                      <div className='knowledge-base-category-list_parent-title'>
                        {category.title}
                      </div>
                    ) : (
                      <h3 className='widget__heading'>{category.title}</h3>
                    )}
                    <div className='knowledge-base-category-list'>
                      {category.subforums.map((subcategory, idx) => (
                        <Card
                          key={subcategory.id}
                          customClass={`knowledge-base__card knowledge-base__card-${
                            this.templates[selectedTemplate || 0]
                          } id-${subcategory.id}`}
                          idx={idx}
                          title={subcategory.title}
                          metadata={
                            <div className='knowledge-base__card-metadata'>
                              <p className='text--meta'>
                                <Phrase
                                  m='Common'
                                  p='knowledge.base.articles'
                                  count={{ n: subcategory.topicCount.toString() }}
                                />
                              </p>
                            </div>
                          }
                          imageSrc={subcategory.thumbnailUrl}
                          navigation={{
                            url: subcategory.url
                          }}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>
      </WidgetContainer>
    )
  }
}

export default KnowledgeBaseWidget
